import {
    IF,
    nil,
    NO,
    UIColor,
    UIDialogView,
    UINativeScrollView,
    UIRectangle,
    UIRoute,
    UITextView,
    UIView,
    YES
} from "uicore-ts"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHFileItemView } from "../Custom components/RH/RHFileItemView"
import { RHLabeledRow } from "../Custom components/RH/RHLabeledRow"
import { RHProcurementTeamMemberRoleDisplayLabels } from "../Custom components/RH/RHTeamRoleSectionView"
import { RHVerticalStackView } from "../Custom components/RH/RHVerticalStackView"
import { RHContractDurationFormatter } from "../Custom components/RH/Utils_slop/RHContractDurationFormatter"
import { formatRHDateTime } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import {
    RHButtons,
    RHColors,
    RHDialogs,
    RHFont,
    RHRadius,
    RHSpacing
} from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHProcurementDetailsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementExcelExportButtonView } from "../ProcurementExcelExport/ProcurementExcelExportButtonView"
import type {
    InvitedCompany,
    RHProcurementCompanyEngagementRow,
    RHProcurementEngagementSummary,
    RHProcurementLifecycleState,
    RHProcurementTeamRole
} from "../SocketClientFunctions"
import type { RHFilePublic, RHProcurement, RHProcurementPublic } from "../../../models/prodModels"
import type { MongooseReference } from "../../../mongooseUtil"
import { populatedValuesOfReferences } from "../Core/RHReference"
import { RHCore } from "../Core/RHCore"
import { RHProcurementPartView } from "./RHProcurementPartView"
import { ShareProcurementDialogView } from "./ShareProcurementDialogView"


type RHProcurementDetailRowKey =
    "publishedAt" |
    "lifecycleState" |
    "referenceNumber" |
    "publicationVisibility" |
    "title" |
    "procuringCompany" |
    "responsibleProcuringUserName" |
    "responsibleProcuringUserEmail" |
    "estimatedCost" |
    "publishEstimatedCostOfProcurement" |
    "deadlineDate" |
    "autoExtend" |
    "contractDuration" |
    "tendererEngagement"


class TendererEngagementMetricView extends UIView {
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontWeight: RHProcurementDetailsViewTokens.detailHeaderFontWeight,
                lineHeight: RHFont.lineHeightBase
            }
        })
        .addedAsSubviewToView(this)
    
    readonly companyNamesAndEmailsLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "-",
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColors.textSecondary,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontWeight: RHProcurementDetailsViewTokens.detailValueFontWeight,
                lineHeight: RHFont.lineHeightBase,
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)
    
    constructor(readonly _title: string) {
        super()
    }
    
    configureWithCompanies(count: number, companies: RHProcurementCompanyEngagementRow[]) {
        this.titleLabel.configureWithObject({
            text: `${this._title} (${count})`
        })
        this.companyNamesAndEmailsLabel.configureWithObject({
            text: TendererEngagementMetricView._companyNamesAndEmailsText(companies)
        })
        this.setNeedsLayoutUpToRootView()
    }
    
    static _companyNamesAndEmailsText(companies: RHProcurementCompanyEngagementRow[]): string {
        if (companies.length === 0) {
            return "-"
        }
        return companies
            .slice()
            .sort((firstCompany, secondCompany) => firstCompany.name.localeCompare(secondCompany.name, "et"))
            .map(company => {
                const companyName = TendererEngagementMetricView._htmlEscapedText(company.name)
                const invitationEmail = company.email?.trim()
                if (!invitationEmail) {
                    return companyName
                }
                return companyName + " (" + TendererEngagementMetricView._htmlEscapedText(invitationEmail) + ")"
            })
            .join(", ")
    }
    
    static _htmlEscapedText(value: string): string {
        return value
            .split("&").join("&amp;")
            .split("<").join("&lt;")
            .split(">").join("&gt;")
            .split("\"").join("&quot;")
            .split("'").join("&#39;")
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        this.contentBoundsWithInset(0)
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.tiny, this.companyNamesAndEmailsLabel)
            .assignedAsFrameOfView(this.companyNamesAndEmailsLabel)
    }
    
}


class TendererEngagementSummaryView extends UIView {
    
    readonly invitedMetricView = new TendererEngagementMetricView("Kutsutud")
        .addedAsSubviewToView(this)
    
    readonly invitedRegisteredMetricView = new TendererEngagementMetricView("Kutsututest registreerunud")
        .addedAsSubviewToView(this)
    
    readonly registeredMetricView = new TendererEngagementMetricView("Registreerunud")
        .addedAsSubviewToView(this)
    
    readonly viewedMetricView = new TendererEngagementMetricView("Hankega tutvunud")
        .addedAsSubviewToView(this)
    
    configureWithSummary(summary: RHProcurementEngagementSummary) {
        this.invitedMetricView.configureWithCompanies(
            summary.invitedCount,
            summary.companies.filter(company => company.invited)
        )
        this.invitedRegisteredMetricView.configureWithCompanies(
            summary.companies.filter(company => company.invited && company.registered).length,
            summary.companies.filter(company => company.invited && company.registered)
        )
        this.registeredMetricView.configureWithCompanies(
            summary.registeredCount,
            summary.companies.filter(company => company.registered)
        )
        this.viewedMetricView.configureWithCompanies(
            summary.viewedCount,
            summary.companies.filter(company => company.viewed)
        )
        this.setNeedsLayoutUpToRootView()
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        this.contentBoundsWithInset(0).framesByDistributingViewsAsColumn(
            [
                this.invitedMetricView,
                this.invitedRegisteredMetricView,
                this.registeredMetricView,
                this.viewedMetricView
            ],
            RHSpacing.small
        )
    }
    
}


class TendererEngagementDialogView extends UIDialogView {
    
    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHColors.white,
            style: {
                borderRadius: `${RHRadius.large}px`,
                boxShadow: "0 10px 35px rgba(0, 0, 0, 0.18)"
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Pakkujate info",
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontWeight: "600",
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly summaryView = new TendererEngagementSummaryView()
        .addedAsSubviewToView(this.contentView)
    
    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge" },
            // Dialog-dismiss buttons use the shared soft-red close language
            // (variant: RHButtons.exitUtilityVariant  // was softWarning / kollane)
            variant: RHButtons.dialogCloseVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    constructor(elementID?: string) {
        super(elementID)
        
        this.view = this.contentView
        this.dismissesOnTapOutside = YES
    }
    
    showWithSummary(summary: RHProcurementEngagementSummary) {
        this.summaryView.configureWithSummary(summary)
        this.showInRootView(YES)
        this.setNeedsLayoutUpToRootView()
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const dialogBounds = this.contentBoundsWithInset(0)
        const dialogWidth = Math.min(padding * 42, dialogBounds.width - padding * 2)
        
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)
        
        const bounds = this.contentView.contentBoundsWithInset(RHSpacing.large)
        const closeButtonWidth = Math.min(
            padding * 8,
            this.closeButton.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) + padding * 2
        )
        
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.large, this.summaryView)
            .assignedAsFrameOfView(this.summaryView)
            .rectangleForNextRow(RHSpacing.large, RHButtons.compactHeight)
            .rectangleWithWidth(closeButtonWidth, 0.5)
            .assignedAsFrameOfView(this.closeButton)
        
        const finalHeight = Math.min(
            this.closeButton.frame.max.y + RHSpacing.large,
            dialogBounds.height - padding * 2
        )
        
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
    }
    
}


/**
 * Value view for the opening-protocol row: shows either the Excel export button
 * or an explanation when a protocol cannot exist because the procurement received
 * no offers. The active value is left-aligned and vertically centered within the
 * height the row assigns it.
 */
class RHProtocolExportValueView extends UIView {

    readonly unavailableExplanationLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Avamise protokolli ei koostatud, kuna pakkumused puuduvad.",
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColors.textSecondary,
            isSingleLine: NO,
            hidden: YES,
            style: {
                fontFamily: RHFont.familyBase,
                fontWeight: RHProcurementDetailsViewTokens.detailValueFontWeight,
                lineHeight: RHFont.lineHeightBase,
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)

    _showsUnavailableExplanation = NO
    
    constructor(readonly exportButton: ProcurementExcelExportButtonView, readonly shareButton: RHButton) {
        super()
        this.exportButton.addedAsSubviewToView(this)
        this.shareButton.addedAsSubviewToView(this)
    }

    get showsUnavailableExplanation(): boolean {
        return this._showsUnavailableExplanation
    }

    set showsUnavailableExplanation(show: boolean) {
        if (this._showsUnavailableExplanation === show) {
            return
        }
        this._showsUnavailableExplanation = show
        this.unavailableExplanationLabel.hidden = !show
        this.exportButton.hidden = show
        this.setNeedsLayoutUpToRootView()
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        if (this._showsUnavailableExplanation) {
            this.contentBoundsWithInset(0)
                .rectangleWithHeight(this.unavailableExplanationLabel, 0.5)
                .assignedAsFrameOfView(this.unavailableExplanationLabel)
            return
        }
        const buttonWidth = this.exportButton.intrinsicContentWidth()
        const buttonHeight = this.exportButton.intrinsicContentHeight(buttonWidth)
        this.contentBoundsWithInset(0)
            .rectangleWithWidth(buttonWidth)
            .rectangleWithHeight(buttonHeight, 0.5)
            .assignedAsFrameOfView(this.exportButton)
            .IF(!this.shareButton.hidden)
            .rectangleForNextColumn(RHSpacing.small, this.shareButton.titleLabel.intrinsicContentWidth() + RHSpacing.medium * 2)
            .rectangleWithHeight(RHDialogs.share.buttonHeight, 0.5)
            .assignedAsFrameOfView(this.shareButton)
            .ENDIF()
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        if (this._showsUnavailableExplanation) {
            return this.unavailableExplanationLabel.intrinsicContentHeight(constrainingWidth)
        }
        const buttonWidth = this.exportButton.intrinsicContentWidth()
        return this.exportButton.intrinsicContentHeight(buttonWidth)
    }
    
}


// What the details view shows: a public procurement, or the owner's / a draft's full one, plus what the page attaches.
export type RHProcurementDetailsData = (RHProcurementPublic | RHProcurement) & {
    engagementSummary?: RHProcurementEngagementSummary
    invitedCompanies?: InvitedCompany[]
    responsibleProcuringUserName?: string
    responsibleProcuringUserEmail?: string
}


export class RHProcurementDetailsView extends UIView {
    
    // Detail fields — each an RHLabeledRow that collapses split↔stacked on its own.
    readonly detailFields: RHLabeledRow[] = []
    readonly detailRowsByKey = {} as Record<RHProcurementDetailRowKey, RHLabeledRow>
    
    /**
     * Card-level action shown when the published procurement can still be amended.
     */
    readonly modifyProcurementButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Muuda andmeid" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactContentPadding,
        hidden: YES,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this.userDidRequestProcurementModification?.()
            }
        }
    })
    
    /**
     * Card-level action letting the responsible procurer end a published
     * procurement without concluding a contract.
     */
    readonly closeProcurementWithoutContractButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Lõpeta ilma lepinguta" },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.compactContentPadding,
        hidden: YES,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this.userDidRequestProcurementClosureWithoutContract?.()
            }
        }
    })
    
    readonly tendererRegistrationWithdrawalButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Tühista registreerimine" },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.compactContentPadding,
        hidden: YES,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this.userDidRequestTendererRegistrationWithdrawal?.()
            }
        }
    })

    /**
     * "Jaga" button in the card's top-right. Visible only for published, public
     * (non-invite-only) procurements. Opens a dialog that copies the link for
     * everyone and adds a send-to-recipients section for logged-in procurers.
     */
    readonly shareButton = new RHButton().configuredWithObject({
        titleLabel: { text: RHDialogs.share.iconMarkup + "Jaga" },
        variant: RHDialogs.share.buttonVariant,
        contentPadding: RHDialogs.share.buttonContentPadding,
        hidden: YES,
        style: {
            fontSize: RHDialogs.share.buttonFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._shareButtonPressed()
            }
        }
    })
    
    readonly currentUserTeamRoleLabel = new UITextView(nil, UITextView.type.paragraph)
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColors.textSecondary,
            isSingleLine: YES,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        })

    readonly notificationsLinkLabel = new UITextView(nil, UITextView.type.paragraph).configuredWithObject({
        textColor: RHColors.textSecondary,
        isSingleLine: YES,
        hidden: YES,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeSmall,
            fontWeight: RHFont.weightRegular,
            lineHeight: RHFont.lineHeightBase
        }
    }).addedAsSubviewToView(this)
    
    readonly tendererEngagementDetailsButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Detailid" },
        variant: RHButtons.exitUtilityVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._openTendererEngagementDialog()
            }
        }
    })
    
    private _shareDialog?: ShareProcurementDialogView
    _tendererEngagementDialog?: TendererEngagementDialogView
    _tendererEngagementSummary?: RHProcurementEngagementSummary
    /**
     * Set by the controller from the server-authoritative `isCurrentUserOnProcuringSide`. Gates the
     * "Saada aadressaatidele" section: only procuring-side users may e-mail the
     * procurement's link; everyone else gets copy-link only.
     */
    isCurrentUserOnProcuringSide = NO
    
    userDidRequestProcurementModification?: () => void
    userDidRequestProcurementClosureWithoutContract?: () => void
    userDidRequestTendererRegistrationWithdrawal?: () => void
    userDidRequestPartClosureWithoutContract?: (partId: string) => void
    userDidRequestShareEmail?: (recipients: string[], link: string) => Promise<void>
    userDidRequestCompanyInvitations?: () => void
    _shareDeadlineTimer?: ReturnType<typeof setTimeout>
    
    /** Controls whether the broad "Muuda andmeid" published-amendment action is available. */
    _canModifyPublishedProcurement = NO
    
    get canModifyPublishedProcurement(): boolean {
        return this._canModifyPublishedProcurement
    }
    
    set canModifyPublishedProcurement(value: boolean) {
        this._canModifyPublishedProcurement = value
        this.modifyProcurementButton.hidden = !value
        this._updateShareButton()
        this.setNeedsLayoutUpToRootView()
    }
    
    _canCloseProcurementWithoutContract = NO
    
    get canCloseProcurementWithoutContract(): boolean {
        return this._canCloseProcurementWithoutContract
    }
    
    set canCloseProcurementWithoutContract(value: boolean) {
        this._canCloseProcurementWithoutContract = value
        this.closeProcurementWithoutContractButton.hidden = !value
        this.setNeedsLayoutUpToRootView()
    }

    _canCloseProcurementPartsWithoutContract = NO

    get canCloseProcurementPartsWithoutContract(): boolean {
        return this._canCloseProcurementPartsWithoutContract
    }

    /** Changing this after data is loaded triggers a re-population of the parts section, same as showParts. */
    set canCloseProcurementPartsWithoutContract(value: boolean) {
        if (this._canCloseProcurementPartsWithoutContract === value) {
            return
        }
        this._canCloseProcurementPartsWithoutContract = value
        if (this._procurementData) {
            this._createPartViews(this._procurementData)
            this.setNeedsLayoutUpToRootView()
        }
    }
    
    /** The tenderer-engagement detail row — hosts the "Detailid" button. */
    get _engagementRow(): RHLabeledRow {
        return this.detailRowsByKey.tendererEngagement
    }
    
    // Parts section — a labeled row whose value is a vertical stack of part views.
    readonly _partsValueStack = new RHVerticalStackView().configuredWithObject({
        rowGap: RHSpacing.small
    })
    
    readonly _partsRow = new RHLabeledRow().configuredWithObject({
        valueFillsMinHeight: YES,
        contentInsets: { left: 0, right: 0, top: RHSpacing.small, bottom: RHSpacing.small }
    }).addedAsSubviewToView(this)
    
    _partViews: RHProcurementPartView[] = []

    shouldShowInvitedCompanies = NO

    readonly _invitedCompaniesRow = new RHLabeledRow().configuredWithObject({
        hidden: YES,
        contentInsets: { left: 0, right: 0, top: RHSpacing.small, bottom: RHSpacing.small },
        headerLabel: { text: "Osalema kutsutavad ettevõtted:", isSingleLine: NO },
        valueView: new UITextView().configuredWithObject({
            textColor: RHColors.textSecondary,
            textAlignment: UITextView.textAlignment.left,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHProcurementDetailsViewTokens.detailValueFontWeight,
                lineHeight: RHFont.lineHeightBase
            },
            textElementView: { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", flexShrink: "0" } }
        })
    }).addedAsSubviewToView(this)
    
    // Opening-protocol section — a labeled row whose value centers the export button.
    readonly openingProtocolExportButtonView = new ProcurementExcelExportButtonView()

    openingProtocolSharingWasRequested?: () => void
    readonly openingProtocolShareButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Tee pakkujatele nähtavaks" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactContentPadding,
        hidden: YES,
        style: { fontSize: RHButtons.compactFontSize },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => this.openingProtocolSharingWasRequested?.()
        }
    })
    
    readonly _openingProtocolValueView = new RHProtocolExportValueView(
        this.openingProtocolExportButtonView, this.openingProtocolShareButton
    )
    
    readonly _openingProtocolRow = new RHLabeledRow().configuredWithObject({
        valueFillsMinHeight: YES
    })
    
    // File section
    readonly filesHeaderLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Lisatud failid",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXXLarge,
                fontWeight: "500",
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    _fileViews: RHFileItemView[] = []
    
    _procurementData?: RHProcurementDetailsData
    _procurementLifecycleState?: RHProcurementLifecycleState
    _procurementInformativeStatusText?: string
    _showParts = true
    _showReferenceNumber = true
    _isPublicationVisibilityShown = YES
    _isOffererView = NO

    get isOffererView(): boolean {
        return this._isOffererView
    }

    set isOffererView(isOffererView: boolean) {
        if (this._isOffererView === isOffererView) {
            return
        }
        this._isOffererView = isOffererView
        this._updatePublicationAndCostRowVisibility()
    }
    
    get showParts(): boolean {
        return this._showParts
    }
    
    /**
     * Set to false before assigning procurementData to prevent part views from
     * being created at all (e.g. when parts are already shown elsewhere).
     * Changing this after data is loaded triggers a re-population of the parts section.
     */
    set showParts(show: boolean) {
        if (this._showParts === show) {
            return
        }
        this._showParts = show
        if (this._procurementData) {
            this._createPartViews(this._procurementData)
            this.setNeedsLayoutUpToRootView()
        }
    }
    
    get showReferenceNumber(): boolean {
        return this._showReferenceNumber
    }
    
    /**
     * Set to false to hide the Viitenumber row (e.g. in the new procurement preview,
     * where the reference number has not yet been assigned).
     */
    set showReferenceNumber(shouldShow: boolean) {
        if (this._showReferenceNumber === shouldShow) {
            return
        }
        this._showReferenceNumber = shouldShow
        this.detailRowsByKey.referenceNumber.hidden = !shouldShow
        this.setNeedsLayoutUpToRootView()
    }

    get isPublicationVisibilityShown(): boolean {
        return this._isPublicationVisibilityShown
    }

    /**
     * Set to NO when publication visibility is selected elsewhere on the same
     * screen, while keeping the row visible by default for published procurements.
     */
    set isPublicationVisibilityShown(shouldShow: boolean) {
        if (this._isPublicationVisibilityShown === shouldShow) {
            return
        }
        this._isPublicationVisibilityShown = shouldShow
        this._updatePublicationAndCostRowVisibility()
    }

    _updatePublicationAndCostRowVisibility() {
        const { publicationVisibility, publishEstimatedCostOfProcurement, estimatedCost } = this.detailRowsByKey
        publicationVisibility.hidden = !this._isPublicationVisibilityShown || this._isOffererView
        publishEstimatedCostOfProcurement.hidden = this._isOffererView
        estimatedCost.hidden = this._isOffererView && this._procurementData?.publishEstimatedCostOfProcurement === NO

        for (const row of [publicationVisibility, publishEstimatedCostOfProcurement, estimatedCost]) {
            if (row.hidden) {
                row.removeFromSuperview()
            }
            else if (row.superview !== this) {
                row.addedAsSubviewToView(this)
            }
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    /** Detail rows currently laid out, in order; hidden rows are skipped. */
    showsProcurementIdentity = YES
    displayedPartId?: string
    numberOfRootProcurementParts?: number

    get _visibleDetailRows(): RHLabeledRow[] {
        const identityRows = [
            this.detailRowsByKey.title,
            this.detailRowsByKey.referenceNumber,
            this.detailRowsByKey.lifecycleState,
            this.detailRowsByKey.publishedAt
        ]
        return this.detailFields.filter(row =>
            !row.hidden && (this.showsProcurementIdentity || !identityRows.includes(row))
        )
    }
    
    get showsOpeningProtocolExport(): boolean {
        return !this._openingProtocolRow.hidden
    }
    
    set showsOpeningProtocolExport(show: boolean) {
        this._openingProtocolRow.hidden = !show
        if (show) {
            this._openingProtocolRow.addedAsSubviewToView(this)
        }
        else {
            this._openingProtocolRow.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }

    get showsOpeningProtocolUnavailableExplanation(): boolean {
        return this._openingProtocolValueView.showsUnavailableExplanation
    }

    set showsOpeningProtocolUnavailableExplanation(show: boolean) {
        this._openingProtocolValueView.showsUnavailableExplanation = show
    }
    
    constructor(elementID?: string) {
        
        super(elementID);
        
        (this as RHProcurementDetailsView).configureWithObject({
            backgroundColor: RHColors.white,
            style: {
                borderRadius: `${RHRadius.large}px`,
                border: `1px solid ${RHColors.borderClassic.stringValue}`,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
            }
        })
        
        this._initializeDetailFields()
        this._initializeStandaloneRows()
        this.filesHeaderLabel.hidden = true
        this.modifyProcurementButton.addedAsSubviewToView(this)
        this.closeProcurementWithoutContractButton.addedAsSubviewToView(this)
        this.tendererRegistrationWithdrawalButton.addedAsSubviewToView(this)
        this.shareButton.addedAsSubviewToView(this)
        
    }
    
    
    /**
     * Configures the parts and opening-protocol rows — the two full-width rows that
     * sit below the detail-field columns rather than inside them.
     */
    _initializeStandaloneRows() {
        this._partsRow.headerLabel.text = "Hankeosad:"
        this._partsRow.valueView = this._partsValueStack
        this._partsRow.hidden = YES
        
        this._openingProtocolRow.headerLabel.text = "Avamise protokoll:"
        this._openingProtocolRow.valueView = this._openingProtocolValueView
        this._openingProtocolRow.hidden = YES
    }
    
    
    // ─── Share ("Jaga") ───────────────────────────────────────────────────────
    
    _shouldShowShareButton(): boolean {
        if (this._procurementData?.status !== "published") {
            return NO
        }
        if (this._procurementData.publicationVisibility === "directed") {
            return this.canModifyPublishedProcurement
        }
        return YES
    }

    _updateShareButton(): void {
        this.shareButton.hidden = !this._shouldShowShareButton()
        this._updateShareButtonAvailability()
    }

    _updateShareButtonAvailability(): void {
        clearTimeout(this._shareDeadlineTimer)
        this._shareDeadlineTimer = undefined
        this.shareButton.enabled = YES
        this.shareButton.hoverText = ""
        if (this.shareButton.hidden || this._procurementData?.publicationVisibility !== "directed") {
            return
        }
        const deadlineDate = this._procurementData.deadlineDate
        const remainingMilliseconds = new Date(deadlineDate ?? 0).getTime() - Date.now()
        if (remainingMilliseconds <= 0 || !Number.isFinite(remainingMilliseconds)) {
            this.shareButton.enabled = NO
            this.shareButton.hoverText = "Pakkumuste esitamise tähtaeg on möödas. Ettevõtteid ei saa enam kutsuda."
            return
        }
        if (this.isMemberOfViewTree) {
            this._shareDeadlineTimer = setTimeout(() => this._updateShareButtonAvailability(), Math.min(remainingMilliseconds, 2147483647))
        }
    }

    override wasAddedToViewTree(): void {
        super.wasAddedToViewTree()
        this._updateShareButtonAvailability()
    }

    override wasRemovedFromViewTree(): void {
        super.wasRemovedFromViewTree()
        clearTimeout(this._shareDeadlineTimer)
        this._shareDeadlineTimer = undefined
    }
    
    configureWithCurrentUserTeamRole(teamRole?: RHProcurementTeamRole) {
        this.currentUserTeamRoleLabel.removeFromSuperview()
        if (teamRole) {
            this.currentUserTeamRoleLabel.text =
                `Roll: ${RHProcurementTeamMemberRoleDisplayLabels[teamRole]}`
            this.currentUserTeamRoleLabel.addedAsSubviewToView(this)
        }
        else {
            this.currentUserTeamRoleLabel.text = ""
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    // Only the procurement's OWNER gets the send-to-recipients section; anonymous
    // visitors, offerers, and procurers viewing OTHER companies' procurements get
    // copy-link only (i.e. behave like an anonymous visitor for sending).
    _canSendProcurementShareEmail(): boolean {
        return this.isCurrentUserOnProcuringSide
    }
    
    // A clean, ownership-agnostic public link to THIS procurement's details,
    // stripped of the current session's back/tab route parameters so an anonymous
    // recipient opens straight onto the procurement.
    _shareLink(): string {
        const baseUrl = ("" + window.location.href).split("#")[0]
        const route = UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["procurement_details"])
            .routeByRemovingParameterInComponent("procurement_details", "backTo")
            .routeByRemovingParameterInComponent("procurement_details", "tab")
        return baseUrl + route.linkRepresentation
    }
    
    _shareButtonPressed() {
        this._updateShareButton()
        if (this.shareButton.hidden || !this.shareButton.enabled) {
            return
        }
        if (this._procurementData?.publicationVisibility === "directed") {
            this.userDidRequestCompanyInvitations?.()
            return
        }
        if (!this._shareDialog) {
            this._shareDialog = new ShareProcurementDialogView()
        }
        this._shareDialog.sendWasRequested = (recipients: string[]) => {
            if (!this.userDidRequestShareEmail) {
                return Promise.resolve()
            }
            return this.userDidRequestShareEmail(recipients, this._shareLink())
        }
        this._shareDialog.show(this._shareLink(), this._canSendProcurementShareEmail())
    }
    
    _openTendererEngagementDialog() {
        if (!this._tendererEngagementSummary) {
            return
        }
        if (!this._tendererEngagementDialog) {
            this._tendererEngagementDialog = new TendererEngagementDialogView()
        }
        this._tendererEngagementDialog.showWithSummary(this._tendererEngagementSummary)
    }
    
    _initializeDetailFields() {
        const fieldConfigs: Array<{ key: RHProcurementDetailRowKey, label: string }> = [
            { key: "publishedAt", label: "Avaldatud:" },
            { key: "lifecycleState", label: "Seisund:" },
            { key: "referenceNumber", label: "Viitenumber:" },
            { key: "publicationVisibility", label: "Avaldamise viis:" },
            { key: "title", label: "Hanke nimetus:" },
            { key: "procuringCompany", label: "Hankija:" },
            { key: "responsibleProcuringUserName", label: "Hanke eest vastutava isiku nimi:" },
            { key: "responsibleProcuringUserEmail", label: "Hanke vastutava isiku e-post:" },
            { key: "estimatedCost", label: "Hanke eeldatav maksumus (KM-ta):" },
            { key: "publishEstimatedCostOfProcurement", label: "Hanke eeldatav maksumus avaldatud:" },
            { key: "deadlineDate", label: "Pakkumuste esitamise tähtaeg:" },
            { key: "autoExtend", label: "Pakkumuste esitamise tähtaja automaatne pikendamine:" },
            { key: "contractDuration", label: "Lepingu kestus kõikidele osadele:" },
            { key: "tendererEngagement", label: "Pakkujate info:" }
        ]
        
        fieldConfigs.forEach(config => {
            const valueLabel = new UITextView()
                .configuredWithObject({
                    text: "-",
                    textAlignment: UITextView.textAlignment.left,
                    textColor: RHColors.textSecondary,
                    style: {
                        fontFamily: RHFont.familyBase,
                        fontWeight: RHProcurementDetailsViewTokens.detailValueFontWeight,
                        lineHeight: RHFont.lineHeightBase
                    }
                })
            
            const row = new RHLabeledRow().addedAsSubviewToView(this)
            row.headerLabel.text = config.label
            row.valueView = valueLabel
            
            this.detailRowsByKey[config.key] = row
            this.detailFields.push(row)
        })
        
        // The procurement title can be long; let it wrap to multiple lines instead of
        // truncating to a single "…" line. The row sizes itself from the value label's
        // intrinsicContentHeight, so it grows to fit the wrapped title.
        this.detailRowsByKey.title.valueLabel.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })
        this.detailRowsByKey.contractDuration.valueLabel.configureWithObject({
            isSingleLine: NO
        })
        this.detailRowsByKey.contractDuration.valueLabel.textElementView.configureWithObject({
            style: { whiteSpace: "pre-wrap" }
        })
        this.detailRowsByKey.responsibleProcuringUserName.valueLabel.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })
        for (const key of [
            "responsibleProcuringUserName", "publishEstimatedCostOfProcurement", "deadlineDate", "autoExtend"
        ] as RHProcurementDetailRowKey[]) {
            this.detailRowsByKey[key].headerLabel.configureWithObject({
                isSingleLine: NO,
                style: { whiteSpace: "normal" }
            })
        }
        
        // The engagement row hosts the "Detailid" button. Its width
        // (label-dependent) is refreshed per layout in _syncRowConfiguration.
        this._engagementRow.configureWithObject({
            accessoryHeight: RHProcurementDetailsViewTokens.editDeadlineButtonHeight,
            contentInsets: { left: 0, right: 0, top: RHSpacing.small, bottom: RHSpacing.small }
        })
        this._engagementRow.valueLabel.isSingleLine = NO
        this._engagementRow.valueLabel.textElementView.configureWithObject({
            // DOM measurement must retain its requested width before the label receives a frame.
            style: { whiteSpace: "pre-wrap", flexShrink: "0" }
        })
        
        this._configureTendererEngagementField(undefined)
        

    }
    
    set procurementData(data: RHProcurementDetailsData | undefined) {
        this._procurementData = data
        this._updatePublicationAndCostRowVisibility()
        this._populateFieldsWithProcurement(data)
    }
    
    get procurementData(): RHProcurementDetailsData | undefined {
        return this._procurementData
    }
    
    get procurementLifecycleState(): RHProcurementLifecycleState | undefined {
        return this._procurementLifecycleState
    }

    set procurementLifecycleState(state: RHProcurementLifecycleState | undefined) {
        this._procurementLifecycleState = state
        this._updateOverviewMetadataFieldsForProcurement(this._procurementData)
        this.setNeedsLayoutUpToRootView()
    }

    set procurementInformativeStatusText(statusText: string | undefined) {
        this._procurementInformativeStatusText = statusText
        this._updateOverviewMetadataFieldsForProcurement(this._procurementData)
        this.setNeedsLayoutUpToRootView()
    }

    _populateFieldsWithProcurement(procurement: RHProcurementDetailsData | undefined) {
        this.notificationsLinkLabel.hidden = !procurement?._id || !RHCore.sharedInstance.userProfile
        if (procurement?._id) {
            const route = UIRoute.currentRoute.routeByRemovingComponentNamed("procurement_details")
                .routeWithComponent("log", {
                    tab: "notifications", procurementId: procurement._id.toString(), referenceNumber: procurement.referenceNumber ?? ""
                })
            this.notificationsLinkLabel.text = 'Hankega seotud teated: <a href="' +
                RHTextSanitizer.escapedHTML(route.linkRepresentation) + '" style="color:' +
                RHColors.primaryBlue.stringValue + '">Vaata</a>'
        }
        this._updateShareButton()
        this._updateOverviewMetadataFieldsForProcurement(procurement)
        
        // Title
        this.detailRowsByKey.title.valueLabel.text = RHTextSanitizer.escapedHTML(procurement?.title || "-")
        
        // Procurer company — only overwrite when the company is populated, so refresh
        // paths that return the procurement without procuringCompany (deadline change,
        // private-info push) keep the name resolved on the first load instead of blanking it.
        const procuringCompany = procurement?.procuringCompany
        const procuringCompanyName = typeof procuringCompany === "object" && procuringCompany !== null && "name" in procuringCompany
            ? procuringCompany.name
            : undefined
        if (procuringCompanyName) {
            this.detailRowsByKey.procuringCompany.valueLabel.text = procuringCompanyName
        }

        this.detailRowsByKey.responsibleProcuringUserName.valueLabel.text =
            RHTextSanitizer.escapedHTML(procurement?.responsibleProcuringUserName?.trim() || procurement?.responsibleProcuringUserEmail?.trim() || "-")
        this.detailRowsByKey.responsibleProcuringUserName.setNeedsLayoutUpToRootView()

        const responsibleProcuringUserEmail = procurement?.responsibleProcuringUserEmail?.trim()
        this.detailRowsByKey.responsibleProcuringUserEmail.hidden = !responsibleProcuringUserEmail
        if (responsibleProcuringUserEmail) {
            this.detailRowsByKey.responsibleProcuringUserEmail.valueLabel.text =
                RHTextSanitizer.escapedHTML(responsibleProcuringUserEmail)
        }
        else {
            this.detailRowsByKey.responsibleProcuringUserEmail.valueLabel.text = ""
        }

        // Reference number — a reconciliation round still gets one internally
        // (see ReconciliationProcurementTitleService), but it isn't meant to
        // be user-facing, so hide the row entirely for a round.
        this.showReferenceNumber = !procurement?.reconciliationParentProcurement
        this.detailRowsByKey.referenceNumber.valueLabel.text = procurement?.referenceNumber || "-"

        let publicationVisibilityText = "-"
        if (procurement) {
            publicationVisibilityText = "Avalik hange"
        }
        if (procurement?.publicationVisibility === "directed") {
            publicationVisibilityText = "Mitteavalik hange"
        }
        this.detailRowsByKey.publicationVisibility.valueLabel.text = publicationVisibilityText
        
        const parts = procurement?.parts ?? []
        const displayedParts = parts.filter(part =>
            !this.displayedPartId || part._id?.toString() === this.displayedPartId
        )
        let totalCost: number | undefined = undefined
        if (displayedParts.length > 0) {
            const costs = displayedParts
                .map(part => part.estimatedCost)
                .filter((cost): cost is number => typeof cost === "number")
            
            if (costs.length > 0) {
                totalCost = costs.reduce((sum: number, cost: number) => sum + cost, 0)
            }
        }
        if (totalCost !== undefined) {
            this.detailRowsByKey.estimatedCost.valueLabel.text = RHEuroFormatter.formattedAmount(totalCost)
        }
        else {
            this.detailRowsByKey.estimatedCost.valueLabel.text = "-"
        }
        
        let publishedCostText = "-"
        if (procurement) {
            publishedCostText = IF(procurement.publishEstimatedCostOfProcurement !== NO)(() => "Jah").ELSE(() => "Ei")
        }
        this.detailRowsByKey.publishEstimatedCostOfProcurement.valueLabel.text = publishedCostText

        // Deadline
        if (procurement?.deadlineDate) {
            const date = new Date(procurement.deadlineDate)
            this.detailRowsByKey.deadlineDate.valueLabel.text = formatRHDateTime(date)
        }
        else {
            this.detailRowsByKey.deadlineDate.valueLabel.text = "-"
        }
        
        // Auto-extend
        if (procurement?.minimumAcceptableNumberOfOffers && procurement.minimumAcceptableNumberOfOffers > 0) {
            this.detailRowsByKey.autoExtend.valueLabel.text =
                `Kui pakkumusi on alla ${procurement.minimumAcceptableNumberOfOffers}, pikeneb tähtaeg ${procurement.numberOfDaysToExtendDeadlineByIfTooFewOffers} päeva võrra.`
        }
        else {
            this.detailRowsByKey.autoExtend.valueLabel.text = "Ei rakendu"
        }
        
        // Contract duration — show the common value or each part's own value.
        const duration = procurement?.contractDuration
        const partsWithIndividualDuration = displayedParts
            .filter(part => !!part.contractDuration)
        const partCount = displayedParts.length
        this.detailRowsByKey.contractDuration.headerLabel.text = IF(partCount > 1)(() =>
            "Lepingu kestus kõikidele osadele:"
        ).ELSE(() => "Lepingu kestus:")
        const allPartsHaveIndividualDuration =
            partsWithIndividualDuration.length > 0 &&
            partsWithIndividualDuration.length === partCount
        
        if (allPartsHaveIndividualDuration) {
            if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
                this.detailRowsByKey.contractDuration.valueLabel.text = partsWithIndividualDuration
                    .map((part, index) => {
                        const durationText = RHContractDurationFormatter.formattedText(
                            part.contractDuration
                        ) ?? "Määramata"
                        return `Osa ${index + 1}: ${durationText}`
                    })
                    .join("\n")
            }
            else {
                this.detailRowsByKey.contractDuration.valueLabel.text =
                    RHContractDurationFormatter.formattedText(
                        partsWithIndividualDuration.firstElement.contractDuration
                    ) ?? "Määramata"
            }
        }
        else {
            this.detailRowsByKey.contractDuration.valueLabel.text =
                RHContractDurationFormatter.formattedText(duration) ?? "Määramata"
        }
        // Procurement data normally arrives after the placeholder row has already
        // been measured. Re-measure the whole row so every per-part line contributes
        // to its intrinsic height before the following detail row is positioned.
        this.detailRowsByKey.contractDuration.setNeedsLayoutUpToRootView()
        
        this._configureTendererEngagementField(procurement?.engagementSummary)
        
        // Create part views
        this._createPartViews(procurement)

        this._invitedCompaniesRow.hidden = !this.shouldShowInvitedCompanies || procurement?.status !== "draft"
        this._invitedCompaniesRow.valueLabel.text = ""
        if (!this._invitedCompaniesRow.hidden) {
            const invitedCompanies: InvitedCompany[] = procurement?.invitedCompanies ?? []
            this._invitedCompaniesRow.valueLabel.text = invitedCompanies
                .filter(company => company.isInvited)
                .map((company, index) => RHTextSanitizer.escapedHTML(
                    `${index + 1}. ${company.name} | ${company.email?.trim() || "E-post puudub"}`
                ))
                .join("\n") || "Puuduvad"
        }
        this._invitedCompaniesRow.setNeedsLayoutUpToRootView()
        
        // Files handling
        this._fileViews.everyElement.removeFromSuperview()
        this._fileViews = []
        
        const files: MongooseReference<RHFilePublic>[] = procurement?.files ?? []
        const populatedFiles = populatedValuesOfReferences(files)
        if (populatedFiles.length > 0) {
            this.filesHeaderLabel.hidden = false
            populatedFiles.forEach(fileData => {
                const fileView = new RHFileItemView().addedAsSubviewToView(this)
                fileView.configureWithFile(fileData, YES, NO)
                this._fileViews.push(fileView)
            })
        }
        else {
            this.filesHeaderLabel.hidden = true
        }
        
        this.setNeedsLayoutUpToRootView()
    }
    
    _updateOverviewMetadataFieldsForProcurement(procurement: RHProcurementDetailsData | undefined) {
        if (procurement?.publishedAt) {
            this.detailRowsByKey.publishedAt.valueLabel.text = formatRHDateTime(
                new Date(procurement.publishedAt)
            )
        }
        else {
            this.detailRowsByKey.publishedAt.valueLabel.text = "-"
        }

        const lifecycleState = this._procurementLifecycleState
        if (!lifecycleState) {
            this.detailRowsByKey.lifecycleState.valueLabel.text = "-"
            this.detailRowsByKey.lifecycleState.valueLabel.textColor = RHColors.textSecondary
            return
        }

        this.detailRowsByKey.lifecycleState.valueLabel.text =
            RHProcurementLifecyclePresentation.detailsTextForLifecycleState(
                lifecycleState,
                this._procurementInformativeStatusText
            )
        this.detailRowsByKey.lifecycleState.valueLabel.textColor = RHColors.textSecondary
    }

    _configureTendererEngagementField(summary: RHProcurementEngagementSummary | undefined) {
        const row = this._engagementRow
        if (!summary) {
            this._tendererEngagementSummary = undefined
            row.hidden = YES
            row.valueLabel.text = ""
            row.trailingAccessory = undefined
            return
        }
        
        this._tendererEngagementSummary = summary
        row.hidden = NO
        row.valueLabel.text = this._tendererEngagementCompactText(summary)
        row.trailingAccessory = this.tendererEngagementDetailsButton
    }
    
    _tendererEngagementCompactText(summary: RHProcurementEngagementSummary): string {
        const invitedRegisteredCount = summary.companies
            .filter(company => company.invited && company.registered)
            .length
        return `Kutsutud: ${summary.invitedCount} · neist registreerunud: ${invitedRegisteredCount}\n` +
            `Registreerunud kokku: ${summary.registeredCount} · hankega tutvunud: ${summary.viewedCount}`
    }
    
    _createPartViews(data: RHProcurementDetailsData | undefined) {
        this._partViews.forEach(view => view.removeFromSuperview())
        this._partViews = []

        if (!this._showParts || !data?.parts || data.parts.length === 0) {
            this._partsRow.hidden = YES
            return
        }

        const partCount = data.parts.length
        const globalDuration = data.contractDuration
        const finalizedPartIds: string[] = data.finalizedPartIds ?? []
        const closedWithoutContractPartIds = data.closedWithoutContractPartIds ?? []

        data.parts.forEach((part, index) => {
            if (this.displayedPartId && part._id?.toString() !== this.displayedPartId) {
                return
            }
            const partView = new RHProcurementPartView(
                this._partsValueStack.elementID + `Part${index}`
            ).addedAsSubviewToView(this._partsValueStack)

            const partId = part._id?.toString()
            partView.configureWithPartData(
                part, YES, globalDuration, partCount,
                data.status === "closed-without-contract" || (!!partId && closedWithoutContractPartIds.includes(partId)),
                this.numberOfRootProcurementParts
            )
            this._partViews.push(partView)

            const isPartClosableWithoutContract = this.canCloseProcurementPartsWithoutContract &&
                partCount > 1 &&
                !!partId &&
                !finalizedPartIds.includes(partId) &&
                !closedWithoutContractPartIds.includes(partId)

            if (isPartClosableWithoutContract) {
                partView.cancelPartButton.configureWithObject({
                    titleLabel: { text: RHProcurementPartDisplay.closeWithoutContractActionButtonText(part, partCount) },
                    hidden: NO,
                    controlEventTargetAccumulator: {
                        PrimaryActionTriggered: () => {
                            this.userDidRequestPartClosureWithoutContract?.(partId)
                        }
                    }
                })
            }
        })

        this._partsRow.headerLabel.text = RHProcurementPartDisplay.detailsPartsHeaderLabelText(partCount)
        this._partsRow.hidden = NO
    }
    
    
    get _visibleCardActionButtons(): RHButton[] {
        return [
            this.modifyProcurementButton,
            this.closeProcurementWithoutContractButton,
            this.tendererRegistrationWithdrawalButton,
            this.shareButton
        ].filter(button => !button.hidden)
    }
    
    
    _cardActionButtonWidthsForButtons(cardActionButtons: RHButton[], availableWidth: number): number[] {
        if (cardActionButtons.length === 0) {
            return []
        }
        
        const preferredButtonWidths = cardActionButtons.map(button =>
            this._preferredCardActionButtonWidth(button)
        )
        const preferredRowWidth = this._combinedCardActionButtonWidth(preferredButtonWidths)
        if (preferredRowWidth <= availableWidth) {
            return preferredButtonWidths
        }
        
        const gapWidth = RHSpacing.small * (cardActionButtons.length - 1)
        const compressedButtonWidth = [
            RHButtons.compactHeight,
            (availableWidth - gapWidth) / cardActionButtons.length
        ].max()
        return cardActionButtons.map(() => compressedButtonWidth)
    }
    
    
    _combinedCardActionButtonWidth(buttonWidths: number[]): number {
        if (buttonWidths.length === 0) {
            return 0
        }
        return buttonWidths.reduce((totalWidth, buttonWidth) =>
            totalWidth + buttonWidth, 0
        ) + RHSpacing.small * (buttonWidths.length - 1)
    }
    
    
    _preferredCardActionButtonWidth(button: RHButton): number {
        return [
            RHDialogs.share.buttonWidth,
            button.titleLabel.intrinsicContentWidth(RHDialogs.share.buttonHeight)
            + RHButtons.compactContentPadding * 2
        ].max()
    }
    
    
    // ─── Layout ───────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(RHSpacing.large)
        const isWideScreen = bounds.width >= 1700
        const cardActionButtons = this._visibleCardActionButtons
        const cardActionButtonWidths = this._cardActionButtonWidthsForButtons(cardActionButtons, bounds.width)
        const cardActionButtonRowWidth = this._combinedCardActionButtonWidth(cardActionButtonWidths)
        let cardMetadataWidth = [RHDialogs.share.buttonWidth, cardActionButtonRowWidth].max()
        if (!this.notificationsLinkLabel.hidden) {
            cardMetadataWidth = [cardMetadataWidth, Math.ceil(this.notificationsLinkLabel.intrinsicContentWidth()) + RHSpacing.medium].max()
        }
        cardMetadataWidth = Math.min(cardMetadataWidth, bounds.width)
        
        // Card-level actions and the current user's team role form the top-right metadata.
        const detailsBounds = bounds
            .IF(cardActionButtons.length > 0)
            .rectangleWithHeight(RHDialogs.share.buttonHeight)
            .rectangleWithWidth(cardActionButtonRowWidth, 1)
            .distributeViewsAlongWidth(
                cardActionButtons,
                undefined,
                RHSpacing.small,
                cardActionButtonWidths
            )
            .ELSE()
            .rectangleWithHeight(0)
            .ENDIF()
            .IF(this.currentUserTeamRoleLabel.superview === this)
            .rectangleWithWidth(cardMetadataWidth, 1)
            .rectangleForNextRow(
                IF(cardActionButtons.length > 0)(() => RHSpacing.tiny).ELSE(() => 0),
                this.currentUserTeamRoleLabel
            )
            .assignedAsFrameOfView(this.currentUserTeamRoleLabel)
            .ENDIF()
            .IF(!this.notificationsLinkLabel.hidden)
            .rectangleWithWidth(cardMetadataWidth, 1)
            .rectangleForNextRow(RHSpacing.tiny, this.notificationsLinkLabel)
            .assignedAsFrameOfView(this.notificationsLinkLabel)
            .ENDIF()
            .IF(
                cardActionButtons.length > 0 ||
                this.currentUserTeamRoleLabel.superview === this || !this.notificationsLinkLabel.hidden
            )
            .rectangleForNextRow(RHDialogs.share.buttonToContentGap, 0)
            .ENDIF()
            .rectangleWithX(bounds.x)
            .rectangleWithWidth(bounds.width)
        
        this._layoutDetailsSection(detailsBounds, isWideScreen, padding)
            .IF(!this.filesHeaderLabel.hidden && this._fileViews.length > 0)
            .rectangleForNextRow(
                RHSpacing.xLarge, RHSpacing.xLarge)
            .assignedAsFrameOfView(this.filesHeaderLabel)
            .rectangleForNextRow(RHSpacing.medium, 0)
            .ENDIF((filesStart: UIRectangle) => {
                const filesBounds = bounds.rectangleWithY(filesStart.y)
                if (isWideScreen) {
                    const halfIndex = Math.ceil(this._fileViews.length / 2)
                    const [leftColumn, rightColumn] = filesBounds.rectanglesByEquallySplittingWidth(2, padding)
                    leftColumn.framesByDistributingViewsAsColumn(this._fileViews.slice(0, halfIndex), padding * 0.5)
                    rightColumn.framesByDistributingViewsAsColumn(this._fileViews.slice(halfIndex), padding * 0.5)
                }
                else {
                    filesBounds.framesByDistributingViewsAsColumn(this._fileViews, padding * 0.5)
                }
                return filesStart
            })
    }
    
    
    // ─── Detail row helpers ───────────────────────────────────────────────────
    
    /** The divider border applied to value labels and split-layout header labels. */
    private get _dividerBorderBottom(): string {
        return `1px solid ${RHColors.dividerAlpha50.stringValue}`
    }
    
    /**
     * Per-layout refresh of row state that depends on live values: the
     * engagement button's width tracks its (localized) label.
     */
    _syncRowConfiguration(padding: number, detailColumnWidth: number) {
        this._engagementRow.accessoryWidth = [
            padding * 6,
            this.tendererEngagementDetailsButton.titleLabel.intrinsicContentWidth(RHButtons.compactHeight)
            + RHButtons.compactContentPadding * 2 + padding
        ].min()
        let engagementTextPaddingRight = 0
        if (detailColumnWidth >= this._engagementRow.splitBreakpoint) {
            engagementTextPaddingRight = this._engagementRow.accessoryWidth + this._engagementRow.accessoryGap
        }
        this._engagementRow.valueLabel.textElementView.configureWithObject({
            style: {
                boxSizing: "border-box",
                paddingRight: engagementTextPaddingRight + "px"
            }
        })
    }
    
    /**
     * Places one self-sizing row as the next full-width row below `afterFrame`,
     * spanning `columnBounds`. The row measures and lays itself out (split vs
     * stacked, dividers, accessory) from the width it is handed.
     */
    _placeRow(row: RHLabeledRow, afterFrame: UIRectangle, columnBounds: UIRectangle): UIRectangle {
        const rowFrame = afterFrame
            .rectangleForNextRow(
                RHProcurementDetailsViewTokens.detailRowGap,
                row.intrinsicContentHeight(columnBounds.width)
            )
            .rectangleWithX(columnBounds.x)
            .rectangleWithWidth(columnBounds.width)
        row.frame = rowFrame
        return rowFrame
    }
    
    /** Stacks a column of detail rows. Returns the bottom Y. */
    _layoutRowColumn(rows: RHLabeledRow[], columnBounds: UIRectangle): number {
        let frame = columnBounds.rectangleWithHeight(0)
        rows.forEach(row => {
            frame = this._placeRow(row, frame, columnBounds)
        })
        return frame.max.y
    }
    
    /**
     * Lays out all detail rows within `containerBounds` — a single column on narrow
     * screens, two equal columns on wide ones — then the full-width parts and
     * opening-protocol rows below. Returns a zero-height rectangle at the bottom.
     */
    private _layoutDetailsSection(containerBounds: UIRectangle, isWideScreen: boolean, padding: number): UIRectangle {
        let detailColumnWidth = containerBounds.width
        if (isWideScreen) {
            detailColumnWidth = (containerBounds.width - padding) / 2
        }
        this._syncRowConfiguration(padding, detailColumnWidth)
        const rows = this._visibleDetailRows
        
        let bottomY: number
        if (!isWideScreen) {
            bottomY = this._layoutRowColumn(rows, containerBounds)
        }
        else {
            const halfIndex = Math.ceil(rows.length / 2)
            const [leftColumn, rightColumn] = containerBounds.rectanglesByEquallySplittingWidth(2, padding)
            bottomY = Math.max(
                this._layoutRowColumn(rows.slice(0, halfIndex), leftColumn),
                this._layoutRowColumn(rows.slice(halfIndex), rightColumn)
            )
        }
        
        // Parts and opening-protocol span the full width, below the column(s).
        let frame = containerBounds.rectangleWithY(bottomY).rectangleWithHeight(0)
        if (!this._partsRow.hidden && this._partViews.length > 0) {
            frame = this._placeRow(this._partsRow, frame, containerBounds)
        }
        return frame
            .IF(!this._invitedCompaniesRow.hidden)
            .rectangleForNextRow(RHProcurementDetailsViewTokens.detailRowGap, this._invitedCompaniesRow)
            .assignedAsFrameOfView(this._invitedCompaniesRow)
            .ENDIF()
            .IF(!this._openingProtocolRow.hidden)
            .rectangleForNextRow(RHProcurementDetailsViewTokens.detailRowGap, this._openingProtocolRow)
            .assignedAsFrameOfView(this._openingProtocolRow)
            .ENDIF()
    }
    
}
